
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { User, Session } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';
import { setupAdminAccount, checkAndSetAdminOnAuth } from '@/utils/adminSetup';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    console.log("AuthProvider mounting - setting up auth state listener");
    
    // Get initial session first
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error("Error getting initial session:", error);
      }
      console.log("Initial session check:", session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // If user is already signed in and is admin, run setup
      if (session?.user?.email === 'hassaanabegam@gmail.com') {
        console.log("Admin user already signed in, running setup");
        setTimeout(() => setupAdminAccount(), 100);
      }
    });
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Only set loading to false after we've processed the auth change
        if (event === 'INITIAL_SESSION') {
          setLoading(false);
        }
        
        // Handle admin setup when user signs in
        if (event === 'SIGNED_IN' && session?.user) {
          console.log("User signed in:", session.user.email);
          setLoading(false);
          
          // Check for admin setup when user signs in
          setTimeout(async () => {
            try {
              await checkAndSetAdminOnAuth(session.user.email || '', session.user.id);
              
              // If this is hassaanabegam@gmail.com, run admin setup
              if (session.user.email === 'hassaanabegam@gmail.com') {
                console.log("Admin user detected, running setup");
                await setupAdminAccount();
              }
            } catch (error) {
              console.error("Error in admin setup:", error);
            }
          }, 100);
        }
        
        if (event === 'SIGNED_OUT') {
          setLoading(false);
          localStorage.removeItem('mockUser');
        }
        
        // Quick init of local storage for mock implementation
        if (session?.user) {
          localStorage.setItem('mockUser', JSON.stringify(session.user));
        }
      }
    );

    return () => {
      console.log("AuthProvider unmounting - unsubscribing from auth state changes");
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      
      console.log("Sign in successful:", data?.user?.id);
      
      if (data && data.user) {
        // Create profile if it doesn't exist
        const { data: profile } = await supabase
          .from('profiles')
          .select()
          .eq('id', data.user.id)
          .maybeSingle();
          
        if (!profile) {
          await supabase.from('profiles').insert({
            id: data.user.id,
            full_name: data.user.user_metadata?.full_name || email.split('@')[0],
            first_name: data.user.user_metadata?.first_name || email.split('@')[0],
            last_name: data.user.user_metadata?.last_name || '',
            avatar_url: data.user.user_metadata?.avatar_url
          });
        }
        
        // Special welcome message for admin
        if (email === 'hassaanabegam@gmail.com') {
          toast({
            title: 'Welcome back, Admin!',
            description: 'You have full access to all admin features and premium content.'
          });
        } else {
          toast({
            title: 'Welcome back!',
            description: 'You have successfully signed in.'
          });
        }
      }
    } catch (error: any) {
      setLoading(false);
      toast({
        title: 'Error',
        description: error.message || 'Failed to sign in',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) throw error;
      
      console.log("Sign up successful:", data?.user?.id);
      
      // Special message for admin signup
      if (email === 'hassaanabegam@gmail.com') {
        toast({
          title: 'Admin account created',
          description: 'Welcome to TeenVisor! You have been granted admin privileges.'
        });
      } else {
        toast({
          title: 'Account created',
          description: 'Welcome to TeenVisor! Your account has been created.'
        });
      }
      
    } catch (error: any) {
      setLoading(false);
      toast({
        title: 'Error',
        description: error.message || 'Failed to sign up',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      console.log("Sign out successful");
      
      toast({
        title: 'Signed out',
        description: 'You have been successfully signed out.'
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to sign out',
        variant: 'destructive'
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, signIn, signUp, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
